import type React from 'react';

import { isMacOs } from '@travel/traveler-core/utils';
import { cx } from '@travel/utils';

import { pushLocation } from 'store/__router/actions';

import { useAppDispatch, useAppSelector } from 'store/hooks';

import { getBaseUrl, getHost, getProtocol } from 'store/__router/selectors';

import styles from './link.module.scss';

import type { MouseEvent, ReactNode, HTMLProps } from 'react';
type CommonProps = {
  children: ReactNode;
  isLink?: boolean;
  customHost?: string;
} & HTMLProps<HTMLAnchorElement>;

export type Props =
  | (CommonProps & { to: string; href?: never })
  | (CommonProps & { href: string; to?: never });

export const getHref = (host: string, to?: string, protocol?: string) => {
  return `${protocol ? `${protocol}://` : ''}${host || ''}${to}`;
};

export function Link(props: Props) {
  const { to, className, isLink, href, children, onClick, customHost, ...restProps } = props;

  const dispatch = useAppDispatch();
  const host = customHost || useAppSelector(getHost);
  const baseUrl = useAppSelector(getBaseUrl) || '';
  const protocol = useAppSelector(getProtocol);

  const handleClick = (e: MouseEvent | React.KeyboardEvent<HTMLAnchorElement>) => {
    const openInNewTab = isMacOs() ? e.metaKey : e.ctrlKey;

    if (!openInNewTab && onClick) {
      onClick(e as MouseEvent<HTMLAnchorElement>);
    } else if (!openInNewTab && to) {
      e.preventDefault();
      dispatch(pushLocation(to));
    }
  };

  return (
    <a
      className={cx(!isLink && styles.wrapper, className)}
      {...restProps}
      onClick={!href ? handleClick : undefined}
      href={href || getHref(host + baseUrl, to, protocol)}
      tabIndex={0}
    >
      {children}
    </a>
  );
}

export default Link;
